import React, { useEffect, useState } from "react";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { refreshAuthSession, useAuthSession } from "../apis/Auth";
import { clearGuestAccess } from "../utils/guestAccess";
import { baseUrl, buildQueryParams } from "../apis/Requests";


export default function Signup({ providerOverride = null, tokenOverride = null }) {
  const navigate = useNavigate();
  const location = useLocation();
  const authQuery = useAuthSession();
  const queryClient = useQueryClient();
  const redirectTo = location.state?.from?.pathname || "/";
  const provider = providerOverride ?? new URLSearchParams(location.search).get("provider");
  const tempToken = tokenOverride ?? new URLSearchParams(location.search).get("temp_token");
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [privacyPolicyAgreed, setPrivacyPolicyAgreed] = useState(false);
  const allAgreed = termsAgreed && privacyPolicyAgreed;

  useEffect(() => {
    if (!authQuery.data?.authenticated) {
      return;
    }

    clearGuestAccess();
    navigate(redirectTo, { replace: true });
  }, [authQuery.data?.authenticated, navigate, redirectTo]);

  const handleSignup = async () => {
    const queryObj = {
      "provider": provider,
      "terms_agreed": termsAgreed,
      "token": tempToken,
      "privacy_policy_agreed": privacyPolicyAgreed
    }
    const queryParams = buildQueryParams(queryObj);

    try {
      const response = await fetch(`${baseUrl}/signin/create?${queryParams.toString()}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json"
        },
      });

      if (response.ok) {
        await refreshAuthSession(queryClient);
        alert("회원 가입이 완료되었습니다. 로그인 페이지로 이동합니다.");
        navigate('/pages/signin', { replace: true });
      } else {
        alert("회원 가입에 실패했습니다. 다시 시도해주세요.");
      }
    } catch (error) {
      alert("회원 가입 중 오류가 발생했습니다. 다시 시도해주세요.");
    }
  };

  return (
    <div className="page-sign">
      <Card className="card-sign">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center mb-4 ">
            <Link to="/" className="header-logo">WHISPER</Link>
            <span>
              <a href="/pages/signin" className="p-0 text-body text-decoration-none fs-3 lh-1 text-end">
               &lt;
              </a>
            </span>
          </div>
          <Card.Title>회원 가입</Card.Title>
          <Card.Text>편한 서비스 이용을 위해 약관 동의 절차를 완료해주세요</Card.Text>
        </Card.Header>
        <Card.Body>
          <Form>
            <Row className="gx-2">
              <Col className="d-flex justify-content-start mb-2" xs={12} sm={12} md={12} lg={12} xl={12}>
                <Form.Check
                  type="checkbox"
                  id="all_terms_agreed"
                  label="약관 전체 동의"
                  checked={allAgreed}
                  onChange={(e) => {
                    const nextChecked = e.target.checked;
                    setTermsAgreed(nextChecked);
                    setPrivacyPolicyAgreed(nextChecked);
                  }}
                />
              </Col>
              <Col className="mb-2" xs={12} sm={12} md={12} lg={12} xl={12}>
                <div className="d-flex justify-content-between align-items-center">
                  <Form.Check
                    type="checkbox"
                    id="terms_agreed"
                    required
                    className="mb-0"
                    label="서비스 이용약관 동의"
                    checked={termsAgreed}
                    onChange={(e) => setTermsAgreed(e.target.checked)}
                  />
                  <Link
                    to="/pages/member-terms"
                    className="d-flex align-items-center text-reset text-decoration-none flex-shrink-0"
                    style={{ paddingLeft: 20, paddingRight: 10 }}
                  >
                    <span>&gt;</span>
                  </Link>
                </div>
              </Col>
              <Col className="mb-4" xs={12} sm={12} md={12} lg={12} xl={12}>
                <div className="d-flex justify-content-between align-items-center">
                  <Form.Check
                    type="checkbox"
                    id="privacy_policy_agreed"
                    required
                    className="mb-0"
                    label="개인정보 처리방침 동의"
                    checked={privacyPolicyAgreed}
                    onChange={(e) => setPrivacyPolicyAgreed(e.target.checked)}
                  />
                  <Link
                    to="/pages/member-policy"
                    className="d-flex align-items-center text-reset text-decoration-none flex-shrink-0"
                    style={{ paddingLeft: 20, paddingRight: 10 }}
                  >
                    <span>&gt;</span>
                  </Link>
                </div>
              </Col>
            </Row>
          </Form>
        </Card.Body>
        <Card.Footer>
          <Button
            type="button" 
            variant="primary" 
            className={`btn-sign ${allAgreed ? 'bg-primary' : 'bg-secondary border-0'}`} 
            disabled={!allAgreed}
            onClick={handleSignup}
          >
            회원 가입
          </Button>
        </Card.Footer>
      </Card>
    </div>
  )
}
